import {
  TypeOfUser,
  adminMap,
  calendarFilter,
  getAdminMap,
  getNearbyProps,
  getPharmacistMap,
  getTypeOfUser,
  mapDate,
  pharmacistMap,
  toType,
  AdminLegendVariant,
  ICalendarEventType,
  getMapUserFilterType,
  MiscType,
} from '@pharmaplan/common';
import { useEffect, useMemo } from 'react';
import { useAppSelector } from '../useAppSelector';
import { useAppDispatch } from '../useAppDispatch';
import { parseMapDate } from '../../helpers/Functions';
import { renderScreen, setOpen } from '../../actions/drawerActions';
import { ScreenTypes } from '../../helpers/Constants';

const { admin, pharmacist, superAdmin, student, technician } = TypeOfUser;
const { booked, requested, posted } = AdminLegendVariant;

const useMap = () => {
  const dispatch = useAppDispatch();
  const userType = useAppSelector(getTypeOfUser);

  const mDate = useAppSelector(mapDate);
  const nearbyProps = useAppSelector(getNearbyProps);
  const filters = useAppSelector(calendarFilter);
  const professionalFilter = useAppSelector(getMapUserFilterType);

  const pharmacistMapEvents = useAppSelector(pharmacistMap);
  const adminMapEvents = useAppSelector(adminMap);

  const mapEvents = {
    [admin]: adminMapEvents?.mapResponse ?? [],
    [superAdmin]: adminMapEvents?.mapResponse ?? [],
    [pharmacist]: pharmacistMapEvents,
    [student]: pharmacistMapEvents,
    [technician]: pharmacistMapEvents,
  };

  const { professionals } = adminMapEvents ?? {};
  const filteredProfessionals = useMemo(
    () =>
      professionals?.filter((item) => {
        if (professionalFilter === MiscType.All) {
          return true;
        }

        return `${item.userType}` === professionalFilter;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [professionalFilter, professionals],
  );

  const getUserBasedMap = () => {
    switch (userType) {
      case pharmacist:
        dispatch(
          getPharmacistMap({
            ...filters,
            weekendVolume:
              typeof filters?.weekendVolume === 'string'
                ? JSON.parse(filters?.weekendVolume)
                : filters?.weekendVolume,
            weekVolume:
              typeof filters?.weekendVolume === 'string'
                ? JSON.parse(filters?.weekVolume)
                : filters?.weekVolume,
            ...parseMapDate(mDate),
            ...nearbyProps,
          }),
        );
        break;
      case admin:
      case superAdmin:
        dispatch(getAdminMap(parseMapDate(mDate)));
        break;

      default:
        break;
    }
  };

  const handleMapNavigation = (
    type: ICalendarEventType,
    eventIds: Array<string>,
    pharmacyId?: string,
    isAutoSyncRecord?: boolean,
  ) => {
    switch (type) {
      case posted:
        dispatch(
          renderScreen({
            eventIds,
            screenNumber: 3,
            pharmacyId,
            screenType: ScreenTypes.availableWorkshift,
            extraParameters: {
              isAutoSyncRecord,
            },
          }),
        );
        break;

      case booked:
        dispatch(
          renderScreen({
            eventIds,
            screenNumber: 2,
            screenType: ScreenTypes.bookingList,
          }),
        );
        break;
      case requested:
        dispatch(
          renderScreen({
            eventIds,
            screenNumber: 2,
            screenType: ScreenTypes.requestedList,
          }),
        );
        break;
      default:
        break;
    }
    dispatch(setOpen(true));
  };

  const handleProfessionalClick = (professionalId: string) =>
    () => {
      dispatch(setOpen(true));
      dispatch(
        renderScreen({
          screenNumber: 2,
          screenType: ScreenTypes.profile,
          pharmacistId: professionalId,
        }),
      );
    };

  useEffect(() => {
    getUserBasedMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mDate, filters, nearbyProps]);

  return {
    mapEvents: mapEvents[toType<typeof mapEvents>(userType)],
    handleMapNavigation,
    professionals: filteredProfessionals ?? [],
    handleProfessionalClick,
  };
};

export default useMap;
